import { useEffect, useState, useRef } from "react";
import { 
  GetDataFromApiWithParams, 
  ExecutePostWithParams 
} from "../../../../services/api/ExecuteApiRequests";
import IPDCFinalTable from "../../../../components/IPDCFinalTable";
import { Box, Button, Alert, CircularProgress, Typography, Card, CardContent } from "@mui/material";
import { Check, Close, Download, TableChart } from "@mui/icons-material";

export default function NewInvestorVerification() {
  const [investors, setInvestors] = useState([]);
  const componentRef = useRef();
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    fetchPendingInvestors();
  }, []);

  const fetchPendingInvestors = async () => {
  try {
    setLoading(true);
    setError("");
    console.log("Fetching pending investors from API...");
    
    // MODIFIED: Added logging and better error handling
    const resultRow = await GetDataFromApiWithParams("investors/pending/approval");
    console.log("Pending Investors API Response:", resultRow);
    
    if (resultRow && Array.isArray(resultRow)) {
      console.log(`Total pending investors received: ${resultRow.length}`);
      
      if (resultRow.length > 0) {
        console.log("First pending investor sample:", resultRow[0]);
        setInvestors(resultRow);
        
        // MODIFIED: Added id column to important columns
        const importantColumns = [
          'id', 'tin_no', 'company_name', 'contact_person', 'contact_person_position',
          'phone_no', 'mobile_no', 'email', 'website', 'nationality_origin',
          'capital', 'description', 'status', 'registration_date'
        ];
        
        const availableColumns = importantColumns.filter(col => 
          resultRow[0].hasOwnProperty(col)
        );
        
        console.log("Columns available for display:", availableColumns);
        setSelectedColumns([...availableColumns, 'actions']);
      } else {
        setInvestors([]);
        setError("No pending investors found for approval.");
      }
    } else {
      setError("No investor data received from API.");
    }
  } catch (err) {
    console.error("Error fetching pending investors:", err);
    setError("Error fetching pending investors: " + (err.message || "Unknown error"));
  } finally {
    setLoading(false);
  }
};

const handleVerification = async (investor, action) => {
  try {
    // MODIFIED: Fixed ID selection - use id first, then tin_no
    const investorId = investor.id || investor.tin_no;
    if (!investorId) {
      setError("Cannot verify: Investor ID is missing");
      return;
    }

    const verificationData = {
      verification_date: new Date().toISOString().split('T')[0],
      remarks: `Investor ${action}ed by Operation Management Head`,
      action: action,
      verified_by: "operation_management_head",
    };

    console.log(`Sending ${action} for investor ID: ${investorId}`);
    
    // MODIFIED: Added logging for debugging
    const response = await ExecutePostWithParams(`investors/${investorId}/verify`, verificationData);
    
    if (response) {
      // MODIFIED: Fixed filtering logic
      setInvestors(prev => prev.filter(item => 
        (item.id || item.tin_no) !== investorId
      ));
      setSuccess(`Investor successfully ${action === "approve" ? "approved" : "rejected"}!`);
      setError("");
      setTimeout(() => setSuccess(""), 5000);
      
      console.log(`Investor ${investorId} ${action}ed successfully`);
    } else {
      setError(`Failed to ${action} investor. Server returned no response.`);
      setSuccess("");
    }
  } catch (err) {
    console.error("Verification error:", err);
    setError(`Error: ${err.message || "Unknown error during verification"}`);
    setSuccess("");
  }
};
  const handleExportToExcel = (investor) => {
    try {
      // Create a CSV content from the investor data
      const headers = Object.keys(investor).filter(key => 
        key !== 'actions' && 
        typeof investor[key] !== 'object' &&
        !key.startsWith('_')
      );
      
      const csvHeaders = headers.join(',');
      const csvRow = headers.map(header => {
        const value = investor[header];
        // Handle values that might contain commas by wrapping in quotes
        return typeof value === 'string' && value.includes(',') ? `"${value}"` : value;
      }).join(',');
      
      const csvContent = `${csvHeaders}\n${csvRow}`;
      
      // Create a Blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `investor_${investor.id || investor.tin_no}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log(`Exported investor ${investor.id || investor.tin_no} to Excel`);
    } catch (err) {
      console.error("Error exporting to Excel:", err);
      setError("Error exporting investor data to Excel");
    }
  };

  const handleExportAllToExcel = () => {
    try {
      if (enhancedInvestors.length === 0) {
        setError("No data to export");
        return;
      }

      // Get all headers from the first investor (excluding actions and objects)
      const headers = Object.keys(enhancedInvestors[0]).filter(key => 
        key !== 'actions' && 
        typeof enhancedInvestors[0][key] !== 'object' &&
        !key.startsWith('_')
      );
      
      // Create CSV headers
      const csvHeaders = headers.join(',');
      
      // Create CSV rows
      const csvRows = enhancedInvestors.map(investor => {
        return headers.map(header => {
          const value = investor[header];
          // Handle values that might contain commas by wrapping in quotes
          return typeof value === 'string' && value.includes(',') ? `"${value}"` : value;
        }).join(',');
      }).join('\n');
      
      const csvContent = `${csvHeaders}\n${csvRows}`;
      
      // Create a Blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `all_pending_investors_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log(`Exported all ${enhancedInvestors.length} investors to Excel`);
      setSuccess(`All ${enhancedInvestors.length} investors exported successfully!`);
      setTimeout(() => setSuccess(""), 5000);
    } catch (err) {
      console.error("Error exporting all to Excel:", err);
      setError("Error exporting all investors data to Excel");
    }
  };

  // Enhanced investors with action buttons
  const enhancedInvestors = investors.map(investor => {
    const investorId = investor.id || investor.tin_no;
    const status = investor.status || 'PENDING';
    
    return {
      ...investor,
      id: investorId,
      status: status,
      actions: (
        <Box sx={{ 
          display: "flex", 
          gap: 1, 
          flexDirection: 'row',
          flexWrap: 'nowrap',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<Download />}
            onClick={() => handleExportToExcel(investor)}
            disabled={!investorId}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Export
          </Button>
          <Button
            variant="contained"
            color="success"
            size="small"
            startIcon={<Check />}
            onClick={() => handleVerification(investor, "approve")}
            disabled={!investorId}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Approve
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            startIcon={<Close />}
            onClick={() => handleVerification(investor, "reject")}
            disabled={!investorId}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Reject
          </Button>
        </Box>
      )
    };
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading pending investors...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
              {success}
            </Alert>
          )}

          {enhancedInvestors.length > 0 ? (
            <>
              <IPDCFinalTable
                ref={componentRef}
                data={enhancedInvestors}
                setData={setInvestors}
                selectedColumns={selectedColumns}
                title={`Pending Investors for Approval (${enhancedInvestors.length})`}
                hasAttachment={false}
              />
              
              {/* Export All Button BELOW the table */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<TableChart />}
                  onClick={handleExportAllToExcel}
                  disabled={enhancedInvestors.length === 0}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Export All Data ({enhancedInvestors.length} rows)
                </Button>
              </Box>
            </>
          ) : (
            !error && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" color="textSecondary">
                  No pending investors found for approval.
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  All investors have been processed or there are no investors waiting for Verification.
                </Typography>
              </Box>
            )
          )}
        </CardContent>
      </Card>
    </Box>
  );
}